// Clima do tempo na tela Início do PWA (pedido 2026-09-22: "clima do tempo
// no sistema, por enquanto só no web e pwa, sem mexer no app") — baseado na
// geolocalização de quem está usando o navegador, sem cadastro de cidade
// novo. Open-Meteo (api.open-meteo.com) é gratuito, sem chave e com CORS
// liberado direto do navegador — não precisa de proxy no backend.
//
// Deliberadamente NÃO reaproveita useUserLocation (use-user-location.ts):
// aquele hook usa watchPosition + enableHighAccuracy contínuo, feito pra
// posicionar o personagem no mapa enquanto a aba Mapa está aberta — ligar
// isso só pra mostrar o clima na Início manteria o GPS ativo à toa. Aqui é
// uma leitura única e grosseira (getCurrentPosition, enableHighAccuracy
// false), suficiente pra clima.
import { useQuery } from "@tanstack/react-query";
import {
  Cloud,
  CloudDrizzle,
  CloudFog,
  CloudLightning,
  CloudMoon,
  CloudRain,
  CloudSnow,
  CloudSun,
  Moon,
  Sun,
  type LucideIcon,
} from "lucide-react";

export interface WeatherNow {
  temperatureC: number;
  feelsLikeC: number;
  weatherCode: number;
  isDay: boolean;
  minC: number;
  maxC: number;
}

export function useGeolocation() {
  return useQuery({
    queryKey: ["weather-geolocation"],
    queryFn: () =>
      new Promise<{ lat: number; lon: number }>((resolve, reject) => {
        if (typeof navigator === "undefined" || !navigator.geolocation) {
          reject(new Error("Geolocalização não suportada"));
          return;
        }
        navigator.geolocation.getCurrentPosition(
          (pos) => resolve({ lat: pos.coords.latitude, lon: pos.coords.longitude }),
          (err) => reject(err),
          { enableHighAccuracy: false, timeout: 10_000, maximumAge: 15 * 60 * 1000 },
        );
      }),
    staleTime: 15 * 60 * 1000,
    gcTime: 60 * 60 * 1000,
    retry: false,
  });
}

// Arredonda a ~1km de precisão — clima não precisa de mais que isso, e
// evita cache-miss a cada leitura de GPS levemente diferente.
function round(n: number): number {
  return Math.round(n * 100) / 100;
}

// Coordenadas usadas pro clima: a localização do aparelho quando existe;
// senão, `fallback` (posição do veículo em destaque na Início). Bug
// relatado 2026-09-22: no app da Play Store o clima nunca aparecia — o
// AndroidManifest do app Capacitor não declara permissão de localização,
// então o WebView nega o GPS na hora. Usar a posição do veículo (que o
// sistema já tem pelo rastreador) faz o clima funcionar em qualquer
// aparelho, sem depender de APK novo nem da pessoa liberar o GPS.
export function useWeatherCoords(
  fallback?: { lat: number; lon: number } | null,
): { lat: number; lon: number } | undefined {
  const geo = useGeolocation();
  if (geo.data) return geo.data;
  if (geo.isError && fallback) return fallback;
  return undefined;
}

export function useWeather(fallback?: { lat: number; lon: number } | null) {
  const coords = useWeatherCoords(fallback);
  const lat = coords ? round(coords.lat) : undefined;
  const lon = coords ? round(coords.lon) : undefined;

  return useQuery({
    queryKey: ["weather", lat, lon],
    queryFn: async (): Promise<WeatherNow> => {
      const url = new URL("https://api.open-meteo.com/v1/forecast");
      url.searchParams.set("latitude", String(lat));
      url.searchParams.set("longitude", String(lon));
      url.searchParams.set("current", "temperature_2m,apparent_temperature,weather_code,is_day");
      url.searchParams.set("daily", "temperature_2m_max,temperature_2m_min");
      url.searchParams.set("timezone", "auto");
      const res = await fetch(url.toString());
      if (!res.ok) throw new Error(`Open-Meteo respondeu ${res.status}`);
      const json = await res.json();
      return {
        temperatureC: Math.round(json.current.temperature_2m),
        feelsLikeC: Math.round(json.current.apparent_temperature),
        weatherCode: json.current.weather_code,
        isDay: json.current.is_day === 1,
        maxC: Math.round(json.daily.temperature_2m_max[0]),
        minC: Math.round(json.daily.temperature_2m_min[0]),
      };
    },
    enabled: lat !== undefined && lon !== undefined,
    staleTime: 15 * 60 * 1000,
    gcTime: 60 * 60 * 1000,
    retry: 1,
    // "Atualizar automaticamente" (pedido 2026-09-22) — refaz sozinho a
    // cada 15min enquanto a tela Início ficar aberta, além do refetch
    // padrão do React Query ao focar a aba de novo.
    refetchInterval: 15 * 60 * 1000,
  });
}

// Tabela de códigos WMO (mesma usada pelo Open-Meteo) → ícone/rótulo em
// pt-BR. https://open-meteo.com/en/docs (seção "WMO Weather interpretation
// codes").
export function describeWeatherCode(
  code: number,
  isDay: boolean,
): { icon: LucideIcon; label: string } {
  switch (code) {
    case 0:
      return { icon: isDay ? Sun : Moon, label: isDay ? "Ensolarado" : "Céu limpo" };
    case 1:
      return { icon: isDay ? CloudSun : CloudMoon, label: "Poucas nuvens" };
    case 2:
      return { icon: isDay ? CloudSun : CloudMoon, label: "Parcialmente nublado" };
    case 3:
      return { icon: Cloud, label: "Nublado" };
    case 45:
    case 48:
      return { icon: CloudFog, label: "Neblina" };
    case 51:
    case 53:
    case 55:
    case 56:
    case 57:
      return { icon: CloudDrizzle, label: "Garoa" };
    case 61:
    case 63:
    case 65:
    case 66:
    case 67:
    case 80:
    case 81:
    case 82:
      return { icon: CloudRain, label: "Chuva" };
    case 71:
    case 73:
    case 75:
    case 77:
    case 85:
    case 86:
      return { icon: CloudSnow, label: "Neve" };
    case 95:
    case 96:
    case 99:
      return { icon: CloudLightning, label: "Trovoada" };
    default:
      return { icon: Cloud, label: "Nublado" };
  }
}

// Tema visual do widget (pedido 2026-09-22: "alterar visual do widget
// conforme a condição") — simplificado nos 3 estados pedidos (ensolarado
// ☀️/nublado ☁️/noite 🌙): à noite sempre o tema "noite" independente do
// código (chuva à noite continua "noite" — os 3 estados pedidos não prevêem
// um 4º tema pra clima ruim à noite); de dia, céu limpo/poucas nuvens (0/1)
// vira "sol", qualquer outra coisa (nublado/chuva/neblina/etc) vira "nuvem".
// O visual em si (céu atrás do topo da Início + card de vidro ao lado dos
// avatares, referências enviadas pelo usuário) fica em mobile-app.tsx.
export type WeatherBucket = "sol" | "nuvem" | "chuva" | "tempestade" | "noite";

// Garoa, chuva, pancadas e trovoada (tabela WMO acima).
export function isRainCode(code: number): boolean {
  return (code >= 51 && code <= 67) || (code >= 80 && code <= 82) || code >= 95;
}

// De dia, chuva ganha tema próprio ("chuva": céu carregado + gotas caindo,
// pedido 2026-09-22 "coloca chovendo pra ver como fica"); à noite continua
// "noite", e a chuva entra só como camada de gotas por cima (ver
// `raining` no InicioSky).
export function weatherBucket(code: number, isDay: boolean): WeatherBucket {
  if (!isDay) return "noite";
  // Trovoada (WMO 95–99) ganha tema próprio — céu escuro, raios.
  if (code >= 95) return "tempestade";
  if (isRainCode(code)) return "chuva";
  if (code === 0 || code === 1) return "sol";
  return "nuvem";
}
